import { useEffect, useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import DOMPurify from 'dompurify'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import ConfirmationModal from '../../common/ConfirmationModal'
import CheckboxInput from '../../common/CheckboxInput'
import Dropdown from '../../common/Dropdown'
import SimpleTextInput from '../../common/SimpleTextInput'
import { toast } from '../../common/Toast'
import TooltipedIcon from '../../common/TooltipedIcon'
import { EditIcon, InformationIcon, TrashCanFull } from '../../../icons'
import { checkPermission, firmwareUploadDeletePermission, firmwareUploadWritePermission } from '../../../utils/permissionValidation'
import { getFileIcon } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import { getRevisionNumberValidation } from '../../../utils/requests/firmwareAPI'
import { useWindowSize } from '../../../libs/hooks'
import { updateRelease, removeFirmwareFile, removeFirmwareDocument, updateFirmwareDocument, updateFirmwareFile } from '../../../actions/firmware'
import '../uploads.scss'

const ViewReleaseModal = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { releaseDetails, handleEditRelease, closeModal, setShowSpinner } = props
  const [instructionFiles, setInstructionFiles] = useState([])
  const [executableFiles, setExecutableFiles] = useState([])
  const [editedFile, setEditedFile] = useState(null)
  const [modelOptions, setModelOptions] = useState([])
  const [fileToDelete, setFileToDelete] = useState(null)
  const [showDeleteFileConfirmModal, setShowDeleteFileConfirmModal] = useState(false)
  const [showDiscardChangesConfirmModal, setShowDiscardChangesConfirmModal] = useState(false)
  const [haveError, setHaveError] = useState({})
  const hasEditPermission = checkPermission(props.userPermissions, firmwareUploadWritePermission)
  const hasDeletePermission = checkPermission(props.userPermissions, firmwareUploadDeletePermission)
  const releaseStatuses = ['inactive', 'active']
  const instructionFirmwareType = 'Instruction'

  useEffect(() => {
    if (releaseDetails) {
      if (releaseDetails.models && releaseDetails.models.length && props.prefixes.length) {
        const matchingPrefix = props.prefixes.find((prefix) => prefix.models.includes(releaseDetails.models[0]))
        setModelOptions([
          ...new Set(
            props.prefixes
              .filter((prefix) => prefix.productType === matchingPrefix?.productType)
              .map((prefix) => prefix.models)
              .flat(),
          ),
        ])
      }

      const releaseInstructionFiles = releaseDetails.files?.filter((file) => file.firmwareType === instructionFirmwareType) || []
      const releaseExecutableFiles = releaseDetails.files?.filter((file) => file.firmwareType !== instructionFirmwareType) || []

      const mappedInstructionFiles = releaseInstructionFiles.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        fileType: file.fileType,
        fileTitle: file.fileTitle || '',
        languageCode: file.languageCode || null,
        firmwareType: instructionFirmwareType,
        revision: file.revision || '',
        isActive: file.isActive || false,
        models: file.models || [],
      }))

      const mappedExecutableFiles = releaseExecutableFiles.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        fileType: file.fileType,
        fileTitle: file.fileTitle || '',
        softwareLabel: file.softwareLabel || '',
        model: file.models?.[0] || null,
        firmwareType: file.firmwareType,
        prefix: file.prefix || null,
        revisionNumber: file.prefixNumber || '',
        isActive: file.isActive || false,
      }))

      setInstructionFiles(mappedInstructionFiles)
      setExecutableFiles(mappedExecutableFiles)
    }
  }, [releaseDetails])

  const releaseHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={releaseDetails.isActive} isDisabled={true} />
        <div className="status">{t(releaseStatuses[Number(releaseDetails.isActive)])}</div>
      </div>
    )
  }

  const releaseFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={closeModal}>
          {t('close')}
        </SimpleButton>
        <SimpleButton className="action-button" onClick={handleEditRelease} requiredPermission={firmwareUploadWritePermission}>
          {t('edit')}
        </SimpleButton>
      </>
    )
  }

  const showDeleteConfirmationModal = (id, isInstructionFile) => {
    setFileToDelete({ id, isInstructionFile })
    setShowDeleteFileConfirmModal(true)
  }

  const handleDeleteFile = () => {
    setShowDeleteFileConfirmModal(false)
    const { id, isInstructionFile } = fileToDelete
    isInstructionFile ? props.actions.removeFirmwareDocument(id, releaseDetails.id) : props.actions.removeFirmwareFile(id, releaseDetails.id)
  }

  const hasFileChanges = () => {
    const initialFile = (editedFile.firmwareType === instructionFirmwareType ? instructionFiles : executableFiles).find((r) => r.id === editedFile.id)
    if (JSON.stringify(editedFile) !== JSON.stringify(initialFile)) {
      return true
    }

    setEditedFile(null)
    return false
  }

  const handleCancelEdit = () => {
    if (!hasFileChanges()) {
      return
    }

    setShowDiscardChangesConfirmModal(true)
  }

  const handleDiscardChanges = () => {
    setShowDiscardChangesConfirmModal(false)
    setEditedFile(null)
    setHaveError({})
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (editedFile.firmwareType === instructionFirmwareType) {
      if (!editedFile.fileTitle.trim()) {
        errors = { ...errors, fileTitle: true }
        isValid = false
      }

      if (!editedFile.languageCode) {
        errors = { ...errors, languageCode: true }
        isValid = false
      }

      if (editedFile.models.length === 0) {
        errors = { ...errors, models: true }
        isValid = false
      }
    } else {
      if (!editedFile.fileTitle.trim()) {
        errors = { ...errors, fileTitle: true }
        isValid = false
      }

      if (!editedFile.softwareLabel.trim()) {
        errors = { ...errors, softwareLabel: true }
        isValid = false
      }

      if (!editedFile.model) {
        errors = { ...errors, model: true }
        isValid = false
      }

      if (!editedFile.firmwareType) {
        errors = { ...errors, firmwareType: true }
        isValid = false
      }

      if (!editedFile.prefix) {
        errors = { ...errors, prefix: true }
        isValid = false
      }

      if (!editedFile.revisionNumber.trim() || haveError[`revisionNumber`]) {
        errors = { ...errors, revisionNumber: true }
        isValid = false
      }
    }

    setHaveError(errors)
    return isValid
  }

  const handleSaveChanges = async () => {
    if (!hasFileChanges()) {
      return
    }

    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    if (editedFile.firmwareType === instructionFirmwareType) {
      const bodyParams = {
        isActive: editedFile.isActive,
        fileTitle: editedFile.fileTitle.trim(),
        languageCode: editedFile.languageCode,
        revision: editedFile.revision.trim(),
        models: editedFile.models,
      }

      props.actions.updateFirmwareDocument(editedFile.id, bodyParams, releaseDetails.id).then(() => {
        setEditedFile(null)
      })

      return
    }

    const bodyParams = {
      isActive: editedFile.isActive,
      fileTitle: editedFile.fileTitle.trim(),
      softwareLabel: editedFile.softwareLabel.trim(),
      firmwareType: editedFile.firmwareType,
      firmwareTypeId: props.types.find((t) => t.type === editedFile.firmwareType)?.id,
      models: [editedFile.model],
      prefix: editedFile.prefix,
      prefixNumber: editedFile.revisionNumber.trim(),
    }

    props.actions.updateFirmwareFile(editedFile.id, bodyParams, releaseDetails.id).then(() => {
      setEditedFile(null)
    })
  }

  const handleFastUpdateStatus = (fileId, isActive, isInstructionFile) => {
    const bodyParams = { isActive }
    if (isInstructionFile) {
      props.actions.updateFirmwareDocument(fileId, bodyParams, releaseDetails.id)

      return
    }

    props.actions.updateFirmwareFile(fileId, bodyParams, releaseDetails.id)
  }

  const handleCheckValidPrefixNo = async () => {
    const initialRevisionNumber = executableFiles.find((f) => f.id === editedFile.id)?.revisionNumber
    if (editedFile.prefix && editedFile.revisionNumber.trim().length > 0 && editedFile.revisionNumber.trim() !== initialRevisionNumber) {
      setShowSpinner(true)

      try {
        const revisionValidationResponse = await getRevisionNumberValidation(editedFile.prefix, editedFile.revisionNumber.trim())

        if (revisionValidationResponse.data) {
          setHaveError((prevError) => ({
            ...prevError,
            [`revisionNumber`]: true,
          }))

          return
        }
      } catch (error) {
        toast.error(t('error.something_wrong'))

        setHaveError((prevError) => ({
          ...prevError,
          [`revisionNumber`]: true,
        }))

        return
      } finally {
        setShowSpinner(false)
      }
    }

    setHaveError((prevError) => ({
      ...prevError,
      [`revisionNumber`]: false,
    }))
  }

  const renderViewInstructionFile = (file) => {
    return (
      <>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {isMobile && (
                <div className="d-flex flex-row with-auto-margin-left align-center">
                  <div
                    className="d-flex flex-row align-center with-margin-right"
                    onClick={() => hasEditPermission && handleFastUpdateStatus(file.id, !file.isActive, true)}
                  >
                    <ToggleSwitch checked={file.isActive} />
                    <div className="without-font-weight">{t(releaseStatuses[Number(file.isActive)])}</div>
                  </div>
                  {hasEditPermission && <EditIcon className="action action-icon" onClick={() => setEditedFile(file)} />}
                  {hasDeletePermission && (
                    <TrashCanFull
                      className={`action-icon ${!hasEditPermission ? 'action' : ''} ${file.isActive ? 'disabled-action' : ''}`}
                      onClick={() => showDeleteConfirmationModal(file.id, true)}
                    />
                  )}
                </div>
              )}
            </p>
            <div className="value">
              {getFileIcon(file.fileName)}
              {file.fileName}
            </div>
          </div>
          {!isMobile && (
            <div className="input-wrapper max-content-width flex-row align-center">
              <div
                className="d-flex flex-row with-auto-margin-left with-margin-right"
                onClick={() => hasEditPermission && handleFastUpdateStatus(file.id, !file.isActive, true)}
              >
                <ToggleSwitch checked={file.isActive} />
                <div className="value">{t(releaseStatuses[Number(file.isActive)])}</div>
              </div>
              {hasEditPermission && <EditIcon className="action action-icon" onClick={() => setEditedFile(file)} />}
              {hasDeletePermission && (
                <TrashCanFull
                  className={`action-icon ${!hasEditPermission ? 'action' : ''} ${file.isActive ? 'disabled-action' : ''}`}
                  onClick={() => showDeleteConfirmationModal(file.id, true)}
                />
              )}
            </div>
          )}
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('file_title')}`}</p>
            <div className="value">{file.fileTitle}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('language')}`}</p>
            <div className="value">{`${t(languagesShort[file.languageCode?.toLowerCase()] || file.languageCode)} (${file.languageCode})`}</div>
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('document_context.revision_number')}`}</p>
            <div className="value">{file.revision}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('monitoring.models')}`}</p>
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper small-columns">
                {file.models?.map((model, index) => (
                  <div className="value" key={index}>
                    {model}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderEditInstructionFile = () => {
    return (
      <>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {isMobile && (
                <div className="action d-flex flex-row without-font-weight align-center with-auto-margin-left">
                  <div className="d-flex flex-row align-center with-margin-right">
                    <ToggleSwitch checked={editedFile.isActive} onChange={() => setEditedFile((prev) => ({ ...prev, isActive: !prev.isActive }))} />
                    <div className="">{t(releaseStatuses[Number(editedFile.isActive)])}</div>
                  </div>
                  <span className="action-label action" onClick={() => handleCancelEdit()}>
                    {t('cancel')}
                  </span>
                  <span className="action-label submit-label" onClick={() => handleSaveChanges()}>
                    {t('save')}
                  </span>
                </div>
              )}
            </p>
            <div className="value">
              {getFileIcon(editedFile.fileName)}
              {editedFile.fileName}
            </div>
          </div>
          {!isMobile && (
            <div className="input-wrapper max-content-width flex-row align-center">
              <div className="d-flex flex-row with-auto-margin-left with-margin-right">
                <ToggleSwitch checked={editedFile.isActive} onChange={() => setEditedFile((prev) => ({ ...prev, isActive: !prev.isActive }))} />
                <div>{t(releaseStatuses[Number(editedFile.isActive)])}</div>
              </div>
              <span className="action-label action" onClick={() => handleCancelEdit()}>
                {t('cancel')}
              </span>
              <span className="action-label submit-label" onClick={() => handleSaveChanges()}>
                {t('save')}
              </span>
            </div>
          )}
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('file_title')}*`}</p>
            <SimpleTextInput
              value={editedFile.fileTitle}
              onChange={(value) => setEditedFile({ ...editedFile, fileTitle: value })}
              onClear={() => setEditedFile({ ...editedFile, fileTitle: '' })}
              hasError={haveError[`fileTitle`]}
            />
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('language')}*`}</p>
            <Dropdown
              placeholder={t('please_select')}
              options={props.languageOptions.map((languageOption) => ({
                key: languageOption,
                value: t(languagesShort[languageOption.toLowerCase()] || languageOption),
              }))}
              initialSelectedKey={editedFile.languageCode}
              onChange={(lang) => setEditedFile({ ...editedFile, languageCode: lang.key })}
              hasError={haveError[`languageCode`]}
            />
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('document_context.revision_number')}`}</p>
            <input
              type="text"
              className={'name-input'}
              value={editedFile.revision || ''}
              onChange={(e) => setEditedFile({ ...editedFile, revision: e.target.value })}
            />
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('monitoring.models')}*`}</p>
            {haveError[`models`] && <span className="error-message">{t('error.must_select_one_model')}</span>}
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {modelOptions.map((model, index) => (
                  <div className="item-wrapper" key={index}>
                    <CheckboxInput
                      checked={editedFile.models.includes(model)}
                      text={model}
                      onChange={() => {
                        setEditedFile({
                          ...editedFile,
                          models: editedFile.models.includes(model) ? editedFile.models.filter((m) => m !== model) : [...editedFile.models, model],
                        })
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderViewSoftwareFile = (file) => {
    return (
      <>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {isMobile && (
                <div className="d-flex flex-row with-auto-margin-left align-center">
                  <div className="d-flex flex-row align-center with-margin-right">
                    <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.active_files_visible')}`} />
                    <div onClick={() => hasEditPermission && handleFastUpdateStatus(file.id, !file.isActive)}>
                      <ToggleSwitch checked={file.isActive} />
                    </div>
                    <div className="without-font-weight">{t(releaseStatuses[Number(file.isActive)])}</div>
                  </div>
                  {hasEditPermission && <EditIcon className="action action-icon" onClick={() => setEditedFile(file)} />}
                  {hasDeletePermission && (
                    <TrashCanFull
                      className={`action-icon ${!hasEditPermission ? 'action' : ''} ${file.isActive ? 'disabled-action' : ''}`}
                      onClick={() => showDeleteConfirmationModal(file.id)}
                    />
                  )}
                </div>
              )}
            </p>
            <div className="value">
              {getFileIcon(file.fileName)}
              {file.fileName}
            </div>
          </div>
          {!isMobile && (
            <div className="input-wrapper max-content-width flex-row align-center">
              <div
                className="d-flex flex-row with-auto-margin-left with-margin-right"
                onClick={() => hasEditPermission && handleFastUpdateStatus(file.id, !file.isActive)}
              >
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.active_files_visible')}`} />
                <ToggleSwitch checked={file.isActive} />
                <div className="value">{t(releaseStatuses[Number(file.isActive)])}</div>
              </div>
              {hasEditPermission && <EditIcon className="action action-icon" onClick={() => setEditedFile(file)} />}
              {hasDeletePermission && (
                <TrashCanFull
                  className={`action-icon ${!hasEditPermission ? 'action' : ''} ${file.isActive ? 'disabled-action' : ''}`}
                  onClick={() => showDeleteConfirmationModal(file.id)}
                />
              )}
            </div>
          )}
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('firmware_context.file_display_title')}</p>
            <div className="value">{file.fileTitle}</div>
          </div>
          <div className="input-wrapper">
            <p className="input-option">{t('firmware_context.device_software_label')}</p>
            <div className="value">{file.softwareLabel}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('model')}</p>
            <div className="value">{file.model}</div>
          </div>
          <div className="input-wrapper">
            <p className="input-option">{t('firmware_context.firmware_type')}</p>
            <div className="value">{file.firmwareType}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('prefix')}</p>
            <div className="value">{file.prefix}</div>
          </div>
          <div className="input-wrapper">
            <p className="input-option">{t('document_context.revision_number')}</p>
            <div className="value">{file.revisionNumber}</div>
          </div>
        </div>
      </>
    )
  }

  const renderEditSoftwareFile = () => {
    return (
      <>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {isMobile && (
                <div className="action d-flex flex-row without-font-weight align-center with-auto-margin-left">
                  <div className="d-flex flex-row align-center with-margin-right">
                    <ToggleSwitch checked={editedFile.isActive} onChange={() => setEditedFile((prev) => ({ ...prev, isActive: !prev.isActive }))} />
                    <div className="">{t(releaseStatuses[Number(editedFile.isActive)])}</div>
                  </div>
                  <span className="action-label action" onClick={() => handleCancelEdit()}>
                    {t('cancel')}
                  </span>
                  <span className="action-label submit-label" onClick={() => handleSaveChanges()}>
                    {t('save')}
                  </span>
                </div>
              )}
            </p>
            <div className="value">
              {getFileIcon(editedFile.fileName)}
              {editedFile.fileName}
            </div>
          </div>
          {!isMobile && (
            <div className="input-wrapper max-content-width flex-row align-center">
              <div className="d-flex flex-row with-auto-margin-left with-margin-right">
                <ToggleSwitch checked={editedFile.isActive} onChange={() => setEditedFile((prev) => ({ ...prev, isActive: !prev.isActive }))} />
                <div>{t(releaseStatuses[Number(editedFile.isActive)])}</div>
              </div>
              <span className="action-label action" onClick={() => handleCancelEdit()}>
                {t('cancel')}
              </span>
              <span className="action-label submit-label" onClick={() => handleSaveChanges()}>
                {t('save')}
              </span>
            </div>
          )}
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('firmware_context.file_display_title')}*`}</p>
            <SimpleTextInput
              value={editedFile.fileTitle}
              onChange={(value) => setEditedFile({ ...editedFile, fileTitle: value })}
              onClear={() => setEditedFile({ ...editedFile, fileTitle: '' })}
              hasError={haveError[`fileTitle`]}
            />
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('firmware_context.device_software_label')}*`}</p>
            <SimpleTextInput
              value={editedFile.softwareLabel}
              onChange={(value) => setEditedFile({ ...editedFile, softwareLabel: value })}
              onClear={() => setEditedFile({ ...editedFile, softwareLabel: '' })}
              hasError={haveError[`softwareLabel`]}
            />
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('model')}*`}</p>
            <Dropdown
              placeholder={t('please_select')}
              options={modelOptions.map((m) => ({ key: m, value: m }))}
              initialSelectedKey={editedFile.model}
              onChange={(model) => setEditedFile({ ...editedFile, model: model.key, firmwareType: null, prefix: null, revisionNumber: '' })}
              hasError={haveError[`model`]}
            />
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('firmware_context.firmware_type')}*`}</p>
            <Dropdown
              placeholder={t('please_select')}
              options={props.types.filter((t) => t.type !== 'Instruction').map((t) => ({ key: t.type, value: t.type }))}
              initialSelectedKey={editedFile.firmwareType}
              onChange={(type) => setEditedFile({ ...editedFile, firmwareType: type.key, prefix: null, revisionNumber: '' })}
              hasError={haveError[`firmwareType`]}
              isDisabled={!editedFile.model}
            />
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('prefix')}*`}</p>
            <Dropdown
              placeholder={t('please_select')}
              options={props.prefixes
                .filter((p) => p.models.includes(editedFile.model) && p.firmwareType === editedFile.firmwareType)
                .map((t) => ({ key: t.prefix, value: t.prefix }))}
              initialSelectedKey={editedFile.prefix}
              onChange={(p) => setEditedFile({ ...editedFile, prefix: p.key, revisionNumber: '' })}
              hasError={haveError[`prefix`]}
              isDisabled={!editedFile.model || !editedFile.firmwareType}
            />
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('document_context.revision_number')}*`}</p>
            <input
              type="text"
              className={'name-input' + (haveError[`revisionNumber`] ? ' has-error' : '')}
              value={editedFile.revisionNumber}
              onChange={(e) => setEditedFile({ ...editedFile, revisionNumber: e.target.value })}
              onBlur={() => handleCheckValidPrefixNo()}
              disabled={!editedFile.prefix}
            />
            {haveError[`revisionNumber`] && editedFile.revisionNumber.length > 0 && (
              <span className="error-message">{t('error.revision_number_already_used')}</span>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={t('details')}
        headerContent={releaseHeaderContent()}
        footerContent={releaseFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('firmware_context.release_name')}`}</p>
              <div className="value">{releaseDetails.recordName}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('description')}</p>
              <div className="value">{releaseDetails.description}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper one-third-width">
              <p className="input-option flex-justify-start">
                {t('tech_access')}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.only_tech')}`} />
              </p>
              <div className="label greyed-out">
                <ToggleSwitch checked={releaseDetails.techAccess} />
                {t(releaseStatuses[Number(releaseDetails.techAccess)])}
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {t('user_access')}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.only_users')}`} />
              </p>
              <div className="label greyed-out">
                <ToggleSwitch checked={releaseDetails.userAccess} />
                {t(releaseStatuses[Number(releaseDetails.userAccess)])}
              </div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('user_notes')}</p>
              <div className="greyed-out" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(releaseDetails.userNotes) }} />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('tech_notes')}</p>
              <div className="greyed-out" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(releaseDetails.techNotes) }} />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('admin_notes')}</p>
              <div className="greyed-out" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(releaseDetails.adminNotes) }} />
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('firmware_context.instruction_files').toUpperCase()}</div>
          {instructionFiles.map((file, index) => (
            <Fragment key={`instruction-${index}`}>
              {' '}
              {index !== 0 && <div className="inner-separator" />}
              {editedFile && editedFile.id === file.id ? renderEditInstructionFile() : renderViewInstructionFile(file)}{' '}
            </Fragment>
          ))}
          <div className="separator" />
          <div className="section-title">{t('firmware_context.firmware_files').toUpperCase()}</div>
          {executableFiles.map((file, index) => (
            <Fragment key={`executable-${index}`}>
              {' '}
              {index !== 0 && <div className="inner-separator" />}
              {editedFile && editedFile.id === file.id ? renderEditSoftwareFile() : renderViewSoftwareFile(file)}{' '}
            </Fragment>
          ))}
        </div>
      </PageModal>
      {showDiscardChangesConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDiscardChangesConfirmModal(false)}
          onAccept={() => handleDiscardChanges()}
          onCancel={() => setShowDiscardChangesConfirmModal(false)}
          message={t('discard_confirmation')}
        />
      )}
      {showDeleteFileConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteFileConfirmModal(false)}
          onAccept={() => handleDeleteFile()}
          onCancel={() => setShowDeleteFileConfirmModal(false)}
          message={t('confirm_delete_file')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={firmwareUploadDeletePermission}
        />
      )}
    </>
  )
}

function stateToProps({ shared, firmware, userPermissions }) {
  return {
    languageOptions: shared?.languages?.map((l) => l.languageCode) || [],
    releaseDetails: firmware?.releaseDetails || {},
    types: firmware?.types || [],
    prefixes: firmware?.prefixes || [],
    userPermissions,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateRelease,
        updateFirmwareDocument,
        updateFirmwareFile,
        removeFirmwareDocument,
        removeFirmwareFile,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ViewReleaseModal)

